import { FileImageOutlined, UploadOutlined } from "@ant-design/icons"
import { Button, Input, message, Upload } from "antd"
import { RcFile } from "antd/es/upload/interface"
import React, { useEffect, useState } from "react"
import { FileUploadData, MediaData } from "~/types/types"
import { configs } from "../../../configs"
import { t } from "../../../languages/i18n"
import { addMedia } from "../../media/services/apiServices"

interface FileUploadProps {
  fileUploadData: FileUploadData
  setFileUploadData: (data: FileUploadData) => void
}

const FileUpload: React.FC<FileUploadProps> = ({ setFileUploadData, fileUploadData }) => {
  const [metaImageUrl, setMetaImageUrl] = useState<string | null>(fileUploadData.metaImageUrl || "")
  const [faviconUrl, setFaviconUrl] = useState<string | null>(fileUploadData.faviconUrl || "")
  const [structuredDataUrl, setStructuredDataUrl] = useState<string | null>(fileUploadData.structuredDataUrl || "")
  const [loading, setLoading] = useState(false)
  const [structuredDataPreview, setStructuredDataPreview] = useState<string | null>(null)

  useEffect(() => {
    setMetaImageUrl(fileUploadData.metaImageUrl)
    setFaviconUrl(fileUploadData.faviconUrl)
    setStructuredDataUrl(fileUploadData.structuredDataUrl)

    if (fileUploadData.structuredDataUrl) {
      fetchJsonContent(fileUploadData.structuredDataUrl)
        .then(content => {
          setStructuredDataPreview(content)
        })
        .catch(error => {
          console.error("Error fetching JSON:", error)
        })
    }
  }, [fileUploadData])

  useEffect(() => {
    setFileUploadData({
      metaImageUrl,
      faviconUrl,
      structuredDataUrl,
    })
  }, [metaImageUrl, faviconUrl, structuredDataUrl, setFileUploadData])

  const fetchJsonContent = async (url: string): Promise<string> => {
    try {
      const response = await fetch(url)
      const jsonData = await response.json()
      return JSON.stringify(jsonData, null, 2)
    } catch (error) {
      console.error("Error fetching JSON:", error)
      throw error
    }
  }

  const readFileContent = (file: RcFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        try {
          const content = reader.result as string
          JSON.parse(content)
          resolve(content)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsText(file)
    })
  }

  const handleUpload = async (file: RcFile, type: "structuredDataUrl" | "metaImage" | "favicon"): Promise<boolean> => {
    setLoading(true)
    const formData = new FormData()
    formData.append("files", file)

    try {
      const response: MediaData[] = await addMedia(formData)

      if (!response || response.length === 0) {
        throw new Error("No response data")
      }

      switch (type) {
        case "structuredDataUrl":
          if (file.type === "application/json") {
            try {
              await readFileContent(file)
              const fileUrl = `${configs.API_URL}${response[0].url}`
              const jsonContent = await fetchJsonContent(fileUrl)
              setStructuredDataPreview(jsonContent)
              setStructuredDataUrl(fileUrl)
              message.success(t("Structured Data uploaded successfully!"))
            } catch (error) {
              message.error(t("Invalid JSON file"))
              return false
            }
          }
          break

        case "metaImage":
          setMetaImageUrl(`${configs.API_URL}${response[0].url}`)
          message.success(t("Meta Image uploaded successfully!"))
          break

        case "favicon":
          setFaviconUrl(`${configs.API_URL}${response[0].url}`)
          message.success(t("Favicon uploaded successfully!"))
          break
      }
      return true
    } catch (error) {
      message.error(`Failed to upload ${type}: ${error}`)
      return false
    } finally {
      setLoading(false)
    }
  }

  const customRequest = async (
    { file, onSuccess, onError }: any,
    type: "structuredDataUrl" | "metaImage" | "favicon",
  ) => {
    try {
      const success = await handleUpload(file as RcFile, type)
      if (success) {
        onSuccess?.()
      } else {
        onError?.(new Error("Upload failed"))
      }
    } catch (error) {
      onError?.(error)
    }
  }

  return (
    <div className="w-full pb-8 border-b flex flex-row">
      <div className="flex flex-col gap-2 items-center w-1/3">
        <Upload
          accept=".json"
          customRequest={options => customRequest(options, "structuredDataUrl")}
          showUploadList={false}
          disabled={loading}
        >
          <Button icon={<UploadOutlined />} loading={loading}>
            {t("Upload Structured Data (JSON)")}
          </Button>
        </Upload>
        {structuredDataUrl && (
          <Input.TextArea
            value={structuredDataPreview ?? ""}
            readOnly
            rows={8}
            placeholder="Structured Data (JSON) content"
            className="mt-4 font-mono text-sm"
          />
        )}
      </div>

      <div className="flex flex-col gap-2 items-center w-1/3">
        <Upload
          accept="image/*"
          customRequest={options => customRequest(options, "metaImage")}
          showUploadList={false}
          disabled={loading}
        >
          <Button icon={<FileImageOutlined />} loading={loading}>
            {t("Upload Meta Image")}
          </Button>
        </Upload>
        {metaImageUrl && (
          <div className="mt-4">
            <img src={metaImageUrl} alt="Meta Image" className="h-24 w-24 object-contain" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 items-center w-1/3">
        <Upload
          accept="image/*"
          customRequest={options => customRequest(options, "favicon")}
          showUploadList={false}
          disabled={loading}
        >
          <Button icon={<FileImageOutlined />} loading={loading}>
            {t("Upload Favicon")}
          </Button>
        </Upload>
        {faviconUrl && (
          <div className="mt-4">
            <img src={faviconUrl} alt="Favicon" className="h-8 w-8 object-contain" />
          </div>
        )}
      </div>
    </div>
  )
}

export default FileUpload
